/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Content 1 PD component — image + copy side-by-side editorial block
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import type { Image } from '@/types';

/* v8 ignore start */
@Component('contentOne', {
    name: 'Content 1',
    description:
        'Editorial split block: image on one side, eyebrow + title + body copy + two CTAs + legal text on the other.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ContentOneMetadata {
    @AttributeDefinition({ id: 'eyebrow', name: 'Eyebrow / Tagline', type: 'string' })
    eyebrow?: string;

    @AttributeDefinition({ name: 'Title', type: 'string' })
    title?: string;

    @AttributeDefinition({ id: 'bodyCopy', name: 'Body Copy', type: 'text' })
    bodyCopy?: string;

    @AttributeDefinition({ id: 'ctaOneText', name: 'Primary CTA Text', type: 'string' })
    ctaOneText?: string;

    @AttributeDefinition({ id: 'ctaOneLink', name: 'Primary CTA Link', type: 'url' })
    ctaOneLink?: string;

    @AttributeDefinition({ id: 'ctaTwoText', name: 'Secondary CTA Text', type: 'string' })
    ctaTwoText?: string;

    @AttributeDefinition({ id: 'ctaTwoLink', name: 'Secondary CTA Link', type: 'url' })
    ctaTwoLink?: string;

    @AttributeDefinition({ id: 'legalText', name: 'Legal Text', type: 'string' })
    legalText?: string;

    @AttributeDefinition({ name: 'Image', type: 'image' })
    image?: Image;

    @AttributeDefinition({ id: 'imageAlt', name: 'Image Alt Text', type: 'string' })
    imageAlt?: string;

    @AttributeDefinition({
        id: 'orientation',
        name: 'Orientation',
        description: 'Which side the image appears on',
        type: 'enum',
        values: ['imageRight', 'imageLeft'],
        defaultValue: 'imageRight',
    })
    orientation?: string;
}
/* v8 ignore stop */

export default function ContentOne({
    eyebrow,
    title,
    bodyCopy,
    ctaOneText,
    ctaOneLink,
    ctaTwoText,
    ctaTwoLink,
    legalText,
    image,
    imageAlt,
    orientation,
}: {
    eyebrow?: string;
    title?: string;
    bodyCopy?: string;
    ctaOneText?: string;
    ctaOneLink?: string;
    ctaTwoText?: string;
    ctaTwoLink?: string;
    legalText?: string;
    image?: Image;
    imageAlt?: string;
    orientation?: string;
}) {
    const { ref, inView } = useInView<HTMLElement>({ margin: '-80px' });
    const imageRight = !orientation || orientation === 'imageRight';

    return (
        <section ref={ref} className="bg-background py-10 lg:py-14">
            <div className="section-container px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    {/* Copy side */}
                    <div
                        className={cn(
                            'flex flex-col gap-6 df-reveal',
                            imageRight ? 'order-2 lg:order-1' : 'order-2',
                            inView && 'df-reveal--show'
                        )}
                        style={{ transitionDelay: inView ? '400ms' : '0ms' }}>
                        {eyebrow && (
                            <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{eyebrow}</p>
                        )}
                        {title && (
                            <h2
                                style={{ fontFamily: 'var(--font-display)' }}
                                className="text-3xl lg:text-4xl font-light leading-snug text-foreground">
                                {title}
                            </h2>
                        )}
                        {bodyCopy && (
                            <p className="text-sm lg:text-base text-muted-foreground leading-relaxed">{bodyCopy}</p>
                        )}
                        {((ctaOneText && ctaOneLink) || (ctaTwoText && ctaTwoLink)) && (
                            <div className="flex flex-wrap gap-3">
                                {ctaOneText && ctaOneLink && (
                                    <Button
                                        asChild
                                        className="df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2">
                                        <Link to={ctaOneLink}>{ctaOneText}</Link>
                                    </Button>
                                )}
                                {ctaTwoText && ctaTwoLink && (
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="rounded-none h-12 min-w-[124px] px-6 py-3 gap-2">
                                        <Link to={ctaTwoLink}>{ctaTwoText}</Link>
                                    </Button>
                                )}
                            </div>
                        )}
                        {legalText && (
                            <p className="text-[11px] text-muted-foreground/70 leading-relaxed">{legalText}</p>
                        )}
                    </div>

                    {/* Image side */}
                    {image?.url && (
                        <div
                            className={cn(
                                'relative overflow-hidden rounded-ui aspect-square bg-muted',
                                imageRight ? 'order-1 lg:order-2' : 'order-1'
                            )}>
                            <DynamicImage
                                src={image.url}
                                alt={imageAlt ?? ''}
                                widths={{ base: 768, lg: 720 }}
                                className="absolute inset-0 w-full h-full"
                                imageProps={{
                                    className:
                                        'w-full h-full object-cover transition-transform duration-700 hover:scale-105',
                                }}
                            />
                            {/* RGBARRY-CUSTOMIZATION: stretched link makes the image clickable to the
                                primary CTA destination, independent of the CTA buttons on the copy side */}
                            {ctaOneText && ctaOneLink && <StretchedLink to={ctaOneLink} />}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
